import { useMemo } from 'react';
import type { ControllerProps } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import type { IPaymentMethod } from '@endeavour/payment-integration';
import { MethodType } from '@endeavour/payment-integration';

import { Button, ButtonSize } from '../../shared/components/button/button';
import type { IComponent, IField } from '../../shared/components/form-builder/form-builder';
import { DisplayType } from '../../shared/components/form-builder/form-builder';
import type { IFlowState } from '../../shared/components/form-flow/form-flow';
import { FormattedMessage } from '../../shared/components/formatted-message/formatted-message';
import { useCurrentCurrency } from '../../shared/hooks/use-current-currency';
import { usePromiseValue } from '../../shared/hooks/use-promise-value';
import { AmountSelector } from '../components/amount-selector/amount-selector';
import { getComponentFromPaymentField, addAmountToSchema } from '../util/fields';
import { useProviderIntegration } from '../util/provider-context';

import { useGetPaymentMethod } from './use-get-payment-method';
import { usePresetList } from './use-preset-list';

export function useDetailsForm({
    providerId,
    accountId,
    integrationId,
    type,
    paymentMethod,
}: {
    providerId: string;
    integrationId: string;
    accountId?: string;
    type: MethodType;
    paymentMethod?: IPaymentMethod;
}) {
    const integration = useProviderIntegration();
    const intl = useIntl();
    const currency = useCurrentCurrency();
    const auth = useAuth();
    const userProfileId = auth.user?.profile.user_profile_id as string;

    const formFields = usePromiseValue(
        useMemo(() => {
            return integration.getMethodDetailsForm({
                configuration: {
                    userProfileId,
                },
                integrationId,
                type,
                providerId,
                accountId,
            });
        }, [type, providerId, accountId, integration, userProfileId, integrationId]),
    );

    const { methodDetails } = useGetPaymentMethod(MethodType.DEPOSIT, paymentMethod, integrationId);

    const methodDetailsData = methodDetails?.data?.[0];

    const previousAmountsList = usePresetList(methodDetailsData).previousAmountsList;

    return useMemo(() => {
        if (!methodDetailsData || formFields.resolving) {
            return;
        }

        if (formFields.data === false) {
            return {
                skip: true,
            };
        }

        const { fields, schema } = formFields.data || { fields: undefined, schema: undefined };

        if (fields === undefined) {
            return;
        }

        const convertedFields = fields.map((field) =>
            getComponentFromPaymentField(field, providerId, integrationId),
        );
        const hasAmountField = convertedFields.some((field) => field.name === 'amount');

        return {
            defaultValues: fields.reduce((currentDefaults, field) => {
                if (field.defaultValue) {
                    currentDefaults[field.name] = field.defaultValue;
                }
                return currentDefaults;
            }, {} as Record<string, unknown>),
            schema: addAmountToSchema(methodDetailsData, schema),
            components: (formData: IFlowState<{ amount: number }>): Array<IField | IComponent> => {
                const amount = formData?.formState?.values?.amount;
                return [
                    ...convertedFields.filter(({ isVisible }) =>
                        isVisible ? isVisible(formData.formState.values) : true,
                    ),
                    ...(!hasAmountField
                        ? [
                              {
                                  name: 'amount',
                                  type: DisplayType.FIELD,
                                  component: Controller,
                                  displayValidation: false,
                                  props: {
                                      render: ({
                                          field: { onChange, onBlur, value },
                                          fieldState: { isTouched, error },
                                      }) => (
                                          <AmountSelector
                                              amounts={previousAmountsList}
                                              currency={currency}
                                              onChange={onChange}
                                              onBlur={onBlur}
                                              value={value}
                                              hasError={!!error}
                                              hasSuccess={!error && isTouched}
                                              message={error?.message}
                                          />
                                      ),
                                  } as ControllerProps,
                              },
                          ]
                        : []),
                    {
                        name: 'button',
                        type: DisplayType.COMPONENT,
                        component: Button,
                        props: {
                            type: 'submit',
                            size: ButtonSize.LARGE,
                            loading: formData.isLoading,
                            disabled: !formData?.formState.validations?.isValid,
                            children: (
                                <FormattedMessage
                                    id={`cashier.${type.toLowerCase()}.submit`}
                                    defaultMessage="Submit {amount}"
                                    values={{
                                        amount: amount
                                            ? intl.formatNumber(amount, {
                                                  style: 'currency',
                                                  currency: currency,
                                                  minimumFractionDigits: 0,
                                              })
                                            : '',
                                    }}
                                />
                            ),
                        },
                    },
                ];
            },
        };
    }, [
        currency,
        integrationId,
        formFields.resolving,
        formFields.data,
        intl,
        type,
        providerId,
        previousAmountsList,
        methodDetailsData,
    ]);
}
